import { useCallback, useMemo, useState } from 'react';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import { EVENTS, SCHEDULE_EVENT_TYPES, RECURRING_EDIT_SCOPE } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import {
  useAddEventResources,
  useRemoveEventResource,
  useAddEventGroupResources,
  useRemoveEventGroupResource,
} from 'shared/hooks/schedule-events.hooks';
import ResourceManager from 'src/components/core/select-content/resource-manager.component';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { RecurringScopeModal } from '../recurring-scope-modal/recurring-scope-modal';
import styles from './event-resources.module.scss';

export function EventResources({ event, canEdit, disabled = false }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const createLang = useSelector(getLang('CREATE_EDUCATIONAL_EVENT'));
  const contentSelectorLang = useSelector(getLang('CONTENT_SELECTOR'));
  const [api, contextHolder] = notification.useNotification();

  const groupId = event?.scheduleEventGroupId;
  const isRecurring = !!groupId;
  const [resourceToRemove, setResourceToRemove] = useState(null);

  const resources = useMemo(() => event?.attributes?.knowledgeItems ?? [], [event]);
  const disabledResourceIds = useMemo(
    () => resources.map(item => item?.id).filter(Boolean),
    [resources],
  );

  const addResources = useAddEventResources();
  const removeResource = useRemoveEventResource();
  const addGroupResources = useAddEventGroupResources();
  const removeGroupResource = useRemoveEventGroupResource();

  const handleAdd = useCallback(
    async (payload, scope) => {
      if (addResources.isPending || addGroupResources.isPending) return;
      const toAll = isRecurring && scope === RECURRING_EDIT_SCOPE.GROUP;
      try {
        if (toAll) {
          await addGroupResources.mutateAsync({ groupId, resources: [payload] });
        } else {
          await addResources.mutateAsync({ eventId: event.id, resources: [payload] });
        }
        analyticsService.track(EVENTS.SCHEDULE.RESOURCE_ADD, {
          type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
          eventId: event.id,
          resourceType: payload?.type,
          recurring: isRecurring,
          ...(isRecurring && {
            scope: toAll ? RECURRING_EDIT_SCOPE.GROUP : RECURRING_EDIT_SCOPE.SINGLE,
            scheduleEventGroupId: groupId,
          }),
        });
        api.success({ message: contentSelectorLang.RESOURCE_ADDED, placement: 'bottomRight' });
      } catch (error) {
        monitorService.error('Failed to add event resource', { error });
        api.error({ message: lang.RESOURCE_ADD_FAILED, placement: 'bottomRight' });
      }
    },
    [
      addResources,
      addGroupResources,
      isRecurring,
      groupId,
      event.id,
      api,
      contentSelectorLang.RESOURCE_ADDED,
      lang.RESOURCE_ADD_FAILED,
    ],
  );

  const removeFromEvent = useCallback(
    async item => {
      try {
        await removeResource.mutateAsync({ eventId: event.id, resourceId: item.id });
        analyticsService.track(EVENTS.SCHEDULE.RESOURCE_REMOVE, {
          type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
          eventId: event.id,
          resourceId: item.id,
          recurring: isRecurring,
          ...(isRecurring && {
            scope: RECURRING_EDIT_SCOPE.SINGLE,
            scheduleEventGroupId: groupId,
          }),
        });
        api.success({ message: contentSelectorLang.RESOURCE_REMOVED, placement: 'bottomRight' });
      } catch (error) {
        monitorService.error('Failed to remove event resource', { error });
        api.error({ message: lang.RESOURCE_REMOVE_FAILED, placement: 'bottomRight' });
      } finally {
        setResourceToRemove(null);
      }
    },
    [
      removeResource,
      event.id,
      isRecurring,
      groupId,
      api,
      contentSelectorLang.RESOURCE_REMOVED,
      lang.RESOURCE_REMOVE_FAILED,
    ],
  );

  const removeFromGroup = useCallback(
    async item => {
      try {
        await removeGroupResource.mutateAsync({ groupId, resourceId: item.id });
        analyticsService.track(EVENTS.SCHEDULE.RESOURCE_REMOVE, {
          type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
          eventId: event.id,
          resourceId: item.id,
          recurring: true,
          scope: RECURRING_EDIT_SCOPE.GROUP,
          scheduleEventGroupId: groupId,
        });
        api.success({ message: contentSelectorLang.RESOURCE_REMOVED, placement: 'bottomRight' });
      } catch (error) {
        monitorService.error('Failed to remove event group resource', { error });
        api.error({ message: lang.RESOURCE_REMOVE_FAILED, placement: 'bottomRight' });
      } finally {
        setResourceToRemove(null);
      }
    },
    [
      removeGroupResource,
      groupId,
      event.id,
      api,
      contentSelectorLang.RESOURCE_REMOVED,
      lang.RESOURCE_REMOVE_FAILED,
    ],
  );

  const handleRemove = useCallback(
    item => {
      if (removeResource.isPending || removeGroupResource.isPending) return;
      if (isRecurring) {
        setResourceToRemove(item);
      } else {
        removeFromEvent(item);
      }
    },
    [isRecurring, removeResource.isPending, removeGroupResource.isPending, removeFromEvent],
  );

  const addScopeOptions = isRecurring
    ? [
        { key: RECURRING_EDIT_SCOPE.GROUP, label: createLang.SCOPE_THIS_AND_FUTURE },
        { key: RECURRING_EDIT_SCOPE.SINGLE, label: createLang.SCOPE_ONLY_THIS },
      ]
    : null;

  return (
    <>
      <ResourceManager
        resources={resources}
        canEdit={canEdit}
        disabled={disabled}
        disabledHint={disabled ? lang.RESOURCES_DISABLED_PAST : ''}
        onAddResource={handleAdd}
        onRemoveResource={handleRemove}
        addScopeOptions={addScopeOptions}
        showTooltipRemoveConfirmation={!isRecurring}
        title={lang.RELATED_RESOURCES}
        titleClassName={styles.resourcesTitle}
        headerClassName={styles.resourcesHeader}
        listClassName={styles.resourcesList}
        modalTitle={createLang.ADD_RESOURCE_TITLE}
        disabledResourceIds={disabledResourceIds}
        contextHolder={contextHolder}
        lang={createLang}
        notFoundText={lang.RESOURCES_EMPTY}
        listWrapperClass={styles.resourceRow}
        emptyInline
      />
      <RecurringScopeModal
        open={!!resourceToRemove}
        title={createLang.SCOPE_REMOVE_RESOURCE_TITLE}
        message={createLang.SCOPE_REMOVE_RESOURCE_MESSAGE}
        lang={createLang}
        danger
        loading={removeResource.isPending || removeGroupResource.isPending}
        onOnlyThis={() => removeFromEvent(resourceToRemove)}
        onAllFuture={() => removeFromGroup(resourceToRemove)}
        onCancel={() => setResourceToRemove(null)}
      />
    </>
  );
}
